import { Color } from 'three';

import glslCnoise from '@/libs/lygia/generative/cnoise.glsl';
import glslFBM from '@/libs/lygia/generative/fbm.glsl';
import glslNoised from '@/libs/lygia/generative/noised.glsl';

export const uniforms = () => ({
    uPeelRadius: { value: 0 },
    uPeelPosX: { value: 0 },
    uPeelPosY: { value: 0 },
    uPeelRotZ: { value: 0 },
});

const normalDisplacementCommon = /* glsl */ `
// progress = smoothstep(0.20, 0.75, progress);
    float flattenStart = -0.6;//-1.0 + progress*2.0;//sin(time*1.0)*.7;
    float flattenSize = 0.3;
    float flattenPower = 0.8;
    float noise = cnoise(meshPosition * 10.0);
    float flattenClamp = smoothstep(0.6, 0.5, abs(meshPosition.y));
    // flattenPower *= flattenClamp;

    float p = worldPosition.y + noise*.02 ;
    float flatten = smoothstep(flattenStart + flattenSize,flattenStart-flattenSize, p);    // ― Normal
    float flattenBottom = smoothstep(flattenStart+flattenSize, flattenStart, p);    // ― Normal
    flattenBottom *= smoothstep(flattenStart - flattenSize, flattenStart, p);    // ― Normal
    float flattenNoisePower = 0.5;
    float flattenNoise = (1.0 - flattenNoisePower) + flattenNoisePower * noise;
    // flatten -=  noise * 2.0;
`;

export const shaderChunks = {
    uniforms: /* glsl */ `
        uniform float uPeelRadius;
        uniform float uPeelPosX;
        uniform float uPeelPosY;
        uniform float uPeelRotZ;
    `,
    vertex: /* glsl */ `
        ${glslFBM}
        ${glslCnoise}

        void rotateAroundPointZ(inout vec3 p , vec3 c, float angle) {
            float si = sin(angle);
            float co = cos(angle);
            // translate point back to origin:
            p.x -= c.x;
            p.y -= c.y;
            // rotate point
            float Xnew = p.x * co - p.y * si;
            float Ynew = p.x * si + p.y * co;
            // // translate point back:
            p.x = Xnew + c.x;
            p.y = Ynew + c.y;
        }

        vec3 applyRipAnimation(vec3 transformed, float peelRotZ, float peelPosX, float peelPosY, float peelRadius) {
            float TAU = PI * 2.0; 
            float cylinderAngle = TAU * peelRotZ;
            vec2 cylinderPosition = vec2(peelPosX, peelPosY);

            vec3 vPosition = transformed;
            vPosition.xy += cylinderPosition;

            rotateAroundPointZ( vPosition, vec3(0., 0., 0.), -cylinderAngle);
            
            float L = vPosition.y;
            float circ = TAU * peelRadius;
            float unitCirc = L/circ;
            float a = (unitCirc * TAU);
            vec2 curve = vec2(peelRadius*cos(a), peelRadius*sin(a));
            float mul = step(0.0, L);
            
            vPosition.z = (curve.x - peelRadius)*mul;
            vPosition.y = mix( vPosition.y, curve.y, mul);
            
            rotateAroundPointZ( vPosition, vec3(0., 0., 0.), cylinderAngle);

            vPosition.xy -= cylinderPosition;
            // vPosition.z = -vPosition.z;

            return vPosition;
        }

        vec3 transition_rip_vertex(vec3 worldPosition, vec3 meshPosition, vec3 direction,  float progress, float time) {
            // worldPosition.y /= 10.0;
            ${normalDisplacementCommon}
            // worldPosition.y *= 10.0;

            vec3 flattenDir = vec3((meshPosition.x), 0.0, meshPosition.z);
            // flattenDir.z = sign(position.z);
            // flattenDir.y = 0.0;
            
            vec3 offset = flatten * flattenDir * 0.05 ;//* flattenClamp;
            return worldPosition + offset;
        }
    `,
    fragment: /* glsl */ `
        ${glslCnoise}
        ${glslFBM}


        vec4 transition_rip_fragment(vec3 worldPosition,vec3 meshPosition, vec3 normal, float progress, float time) {
            float startY = 0.597;
            float range = 0.032;
            float endY = startY + range;
            float falloff = 0.005;
            float finalColor = smoothstep(startY - falloff, startY, meshPosition.y) * smoothstep(endY + falloff, endY , meshPosition.y) * .5;
            range = 0.2; // 0.2
            float wave = sin(u_time*10.0- meshPosition.x * 20.0) *.25 + 1.0;
            finalColor *= min(1.0, max(0.0, progress*10.0 - meshPosition.x-0.38145) * wave)* min(1.0, progress * 10.0);
            finalColor += smoothstep(startY - range, startY, meshPosition.y) * smoothstep(startY + range, startY , meshPosition.y) * .1 * wave * min(1.0, progress * 10.0);
            finalColor = min(1.0, pow(finalColor, 2.2));
            
            float hue = meshPosition.y * 30.0 + .4 - u_time * .1;
            vec3 rainbowColor = vec3(0.0, 0.8, 1.0) * 5.0;//hsv2rgb(vec3(fract(hue), 0.5, 1.0));
            if(!gl_FrontFacing) {
                float r = smoothstep(0.55, 0.5, meshPosition.y);
                float g = smoothstep(0.65, 0.5, meshPosition.y);
                float b = smoothstep(0.75, 0.5, meshPosition.y);

                float dotView = dot(normal, vec3(0.0, 0.0, 1.0));
                rainbowColor = vec3(r,g,b);// -vec3(meshPosition.y) * 2.0 - 1.0;
                finalColor = 1.0;
            }
            return vec4(rainbowColor, finalColor);
        }

        vec3 transition_rip_normal_fragment(vec3 worldPosition,vec3 meshPosition, vec3 normal, float progress, float time) {
            ${normalDisplacementCommon}

            normal += (vec3(0.0, -1.0, 0.0)-normal) * flattenBottom * flattenPower * 0.4;
            normal += (vec3(0.0, 0.0, 1.0)-normal) * flatten * flattenPower ;
            
            normal = normalize(normal);
            return normal;
        }
    `,
};
